import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { UserPlus, Trash2, Edit2, Search, Users2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { EMPLOYEE_CATEGORIES, empCatMeta } from '@/lib/expenseCategories';

export default function EmployeesManager() {
  const [employees, setEmployees] = useState([]);
  const [query, setQuery] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/employees');
      setEmployees(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal memuat'); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = employees;
    if (catFilter !== 'all') list = list.filter((e) => e.category === catFilter);
    if (query) {
      const q = query.toLowerCase();
      list = list.filter((e) => (e.name || '').toLowerCase().includes(q) || (e.phone || '').toLowerCase().includes(q));
    }
    return list;
  }, [employees, query, catFilter]);

  const counts = useMemo(() => {
    const c = {};
    EMPLOYEE_CATEGORIES.forEach((k) => { c[k.value] = 0; });
    employees.forEach((e) => { if (c[e.category] !== undefined) c[e.category] += 1; });
    return c;
  }, [employees]);

  const doDelete = async (e) => {
    try {
      const { data } = await api.delete(`/employees/${e.id}`);
      const orphan = data?.orphaned_salary_records || 0;
      if (orphan > 0) {
        toast.success(`Karyawan "${e.name}" dihapus. ${orphan} riwayat gaji tetap tersimpan.`);
      } else {
        toast.success(`Karyawan "${e.name}" dihapus`);
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Gagal hapus'); }
  };

  return (
    <div className="space-y-4">
      {/* Counter cards */}
      <div className="flex flex-wrap gap-2">
        {EMPLOYEE_CATEGORIES.map((c) => (
          <Badge key={c.value} className={`${c.color} border-0 px-3 py-1`} data-testid={`emp-count-${c.value}`}>
            <c.icon className="h-3.5 w-3.5 mr-1" />
            {c.label}: <b className="ml-1 tabular-nums">{counts[c.value] || 0}</b>
          </Badge>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex flex-col sm:flex-row gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Cari nama karyawan / telepon..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" data-testid="emp-search" />
          </div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-full sm:w-44" data-testid="emp-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kategori</SelectItem>
              {EMPLOYEE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="emp-add-button"><UserPlus className="h-4 w-4 mr-2" />Tambah Karyawan</Button>
          </DialogTrigger>
          <EmployeeFormDialog editing={editing} onSaved={() => { load(); setOpen(false); setEditing(null); }} />
        </Dialog>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">Belum ada karyawan terdaftar.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="emp-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Nama</TableHead>
                    <TableHead>Kategori</TableHead>
                    <TableHead>Telepon</TableHead>
                    <TableHead>Catatan</TableHead>
                    <TableHead>Didaftarkan Oleh</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((e) => {
                    const em = empCatMeta(e.category);
                    const Icon = em.icon;
                    return (
                      <TableRow key={e.id} data-testid={`emp-row-${e.id}`}>
                        <TableCell className="font-medium">{e.name}</TableCell>
                        <TableCell>
                          <Badge className={`${em.color} border-0`}>
                            <Icon className="h-3 w-3 mr-1" />
                            {em.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{e.phone || '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate" title={e.note || ''}>{e.note || '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{e.created_by_name || '-'}</TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="sm" variant="outline" onClick={() => { setEditing(e); setOpen(true); }} data-testid={`emp-edit-${e.id}`}>
                            <Edit2 className="h-3.5 w-3.5 mr-1" />Edit
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="outline" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200" data-testid={`emp-delete-${e.id}`}>
                                <Trash2 className="h-3.5 w-3.5 mr-1" />Hapus
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Hapus karyawan "{e.name}"?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Karyawan akan dihapus dari daftar. Riwayat gaji tetap tersimpan (recipient_name snapshot).
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Batal</AlertDialogCancel>
                                <AlertDialogAction onClick={() => doDelete(e)} className="bg-rose-600 hover:bg-rose-700" data-testid={`emp-delete-confirm-${e.id}`}>Ya, Hapus</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function EmployeeFormDialog({ editing, onSaved }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('office');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editing) {
      setName(editing.name || '');
      setCategory(editing.category || 'office');
      setPhone(editing.phone || '');
      setNote(editing.note || '');
    } else {
      setName(''); setCategory('office'); setPhone(''); setNote('');
    }
  }, [editing]);

  const submit = async () => {
    if (!name.trim()) return toast.error('Nama karyawan wajib diisi');
    setSubmitting(true);
    try {
      if (editing) {
        await api.patch(`/employees/${editing.id}`, { name: name.trim(), category, phone, note });
        toast.success('Karyawan diperbarui');
      } else {
        await api.post('/employees', { name: name.trim(), category, phone: phone.trim() || null, note: note.trim() || null });
        toast.success(`Karyawan "${name}" ditambahkan`);
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal simpan'); }
    finally { setSubmitting(false); }
  };

  return (
    <DialogContent className="sm:max-w-md" data-testid="emp-form-dialog">
      <DialogHeader><DialogTitle>{editing ? 'Edit Karyawan' : 'Tambah Karyawan Baru'}</DialogTitle></DialogHeader>
      <div className="space-y-3 py-2">
        <div className="space-y-1.5">
          <Label>Nama Karyawan <span className="text-rose-500">*</span></Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="mis. Ahmad Yusuf" data-testid="emp-form-name" />
        </div>
        <div className="space-y-1.5">
          <Label>Kategori <span className="text-rose-500">*</span></Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="emp-form-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EMPLOYEE_CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value} data-testid={`emp-form-cat-${c.value}`}>
                  <div className="flex items-center gap-2"><c.icon className="h-3.5 w-3.5" />{c.label}</div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Telepon (opsional)</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="mis. 08123456789" data-testid="emp-form-phone" />
        </div>
        <div className="space-y-1.5">
          <Label>Catatan (opsional)</Label>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Posisi, alamat, dll" data-testid="emp-form-note" />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={submitting} data-testid="emp-form-save">
          {submitting ? 'Menyimpan...' : (editing ? 'Simpan' : 'Tambah Karyawan')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
